/**
 * DeviceFrame — wraps project images in a device chrome + outline frame.
 *
 * Frame has a padding gap between the slide edge and the visible border,
 * no background fills — outline-only aesthetic throughout.
 *
 * Swiper with fade effect lives inside the image area only — the chrome
 * (browser bar, bezels, home indicator) never participates in transitions.
 */
import { getImageUrl } from "@/lib/images"
import { cn } from "@/lib/utils"
import type { ProjectImage } from "@/types/portfolio"
import { useEffect, useRef } from "react"
import { Swiper, SwiperSlide } from "swiper/react"
import { EffectFade } from "swiper/modules"
import type { Swiper as SwiperClass } from "swiper"
import "swiper/swiper.css"

export type DeviceType = "desktop" | "tablet" | "mobile" | "generic" | "print"

const BORDER = "2px dashed rgba(255,255,255,0.1)"
const CHROME_SEP = "1px solid rgba(255,255,255,0.07)"
export const FRAME_PADDING = 12 // px gap between slide edge and device border

// ─── DeviceFrame ─────────────────────────────────────────────────────────────

interface DeviceFrameProps {
  type: DeviceType
  images: ProjectImage[]
  activeIdx: number
  onSlideChange?: (idx: number) => void
  className?: string
}

export function DeviceFrame({ type, images, activeIdx, onSlideChange, className }: DeviceFrameProps) {
  const swiperRef = useRef<SwiperClass | null>(null)
  const hasRealImage = images.some((img) => Boolean(img.filename && img.ext))

  // Sync external activeIdx → Swiper (guard prevents loop)
  useEffect(() => {
    const swiper = swiperRef.current
    if (!swiper || swiper.activeIndex === activeIdx) return
    swiper.slideTo(activeIdx)
  }, [activeIdx])

  return (
    <div
      data-device-frame
      data-device-type={type}
      className={cn("select-none flex flex-col overflow-hidden", className)}
      style={type === "print"
        ? { borderRadius: "16px" }
        : { border: BORDER, borderRadius: "10px", padding: `${FRAME_PADDING}px` }}
    >
      {type !== "print" && <ChromeTop type={type} hasRealImage={hasRealImage} />}
      <div data-device-screen className="relative flex-1 overflow-hidden" style={{ borderRadius: type === "print" ? "14px" : "3px" }}>
        {images.length === 0 ? (
          <Placeholder />
        ) : (
          <Swiper
            modules={[EffectFade]}
            effect="fade"
            fadeEffect={{ crossFade: true }}
            speed={1200}
            allowTouchMove={false}
            preventClicks={false}
            preventClicksPropagation={false}
            onSwiper={(s) => { swiperRef.current = s }}
            onSlideChange={(s) => onSlideChange?.(s.activeIndex)}
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
          >
            {images.map((img) => {
              const url = getImageUrl(img, "large")
              return (
                <SwiperSlide key={img.id} className="h-full w-full">
                  <SlideContent src={url} isVideo={img.ext === "webm" || img.ext === "mp4"} />
                </SwiperSlide>
              )
            })}
          </Swiper>
        )}
      </div>
      {type !== "print" && <ChromeBottom type={type} />}
    </div>
  )
}

// ─── SlideContent ─────────────────────────────────────────────────────────────

function SlideContent({ src, isVideo }: { src: string | null; isVideo?: boolean }) {
  if (!src) return <Placeholder />
  return (
    <div className="absolute inset-0">
      {isVideo ? (
        <video
          src={src}
          className="block h-full w-full"
          style={{
            objectFit: "cover",
            objectPosition: "top center",
            filter: "contrast(1.06) brightness(0.92)",
          }}
          autoPlay
          loop
          muted
          playsInline
        />
      ) : (
        <img
          src={src}
          alt=""
          className="block h-full w-full"
          style={{
            objectFit: "cover",
            objectPosition: "top center",
            filter: "contrast(1.06) brightness(0.92)",
          }}
          decoding="async"
        />
      )}
      {/* CRT scanline overlay */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.08) 2px, rgba(0,0,0,0.08) 4px)",
          mixBlendMode: "multiply",
        }}
      />
      {/* Vignette */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background: "radial-gradient(ellipse at center, transparent 55%, rgba(0,0,0,0.45) 100%)",
        }}
      />
    </div>
  )
}

// ─── Placeholder ──────────────────────────────────────────────────────────────

function Placeholder() {
  return (
    <div className="absolute inset-0 flex items-center justify-center">
      <span className="text-xs tracking-widest text-white/20 uppercase">Coming soon</span>
    </div>
  )
}

// ─── Chrome ───────────────────────────────────────────────────────────────────

function ChromeTop({ type, hasRealImage }: { type: DeviceType; hasRealImage: boolean }) {
  if (type === "desktop") return <DesktopChrome hasRealImage={hasRealImage} />
  if (type === "tablet")  return <TabletTopBezel />
  if (type === "generic") return null
  return <MobileTopBezel />
}

function ChromeBottom({ type }: { type: DeviceType }) {
  if (type === "desktop") return null
  if (type === "tablet")  return <TabletBottomBezel />
  if (type === "generic") return null
  return <MobileHomeBar />
}

function DesktopChrome({ hasRealImage }: { hasRealImage: boolean }) {
  return (
    <div className="flex shrink-0 items-center gap-2 pb-3" style={{ borderBottom: hasRealImage ? "none" : CHROME_SEP }}>
      {/* mac dots */}
      <div className="flex items-center gap-1.5">
        <div className="h-3 w-3 rounded-sm" style={{ border: CHROME_SEP }} />
        <div className="h-3 w-3 rounded-sm" style={{ border: CHROME_SEP }} />
        <div className="h-3 w-3 rounded-sm" style={{ border: CHROME_SEP }} />
      </div>
      {/* url bar */}
      <div className="h-3 flex-1 rounded" style={{ border: CHROME_SEP }} />
      {/* right icons */}
      <div className="flex items-center gap-1">
        <div className="h-3 w-3 rounded-sm" style={{ border: CHROME_SEP }} />
        <div className="h-3 w-3 rounded-sm" style={{ border: CHROME_SEP }} />
        <div className="h-3 w-3 rounded-sm" style={{ border: CHROME_SEP }} />
      </div>
    </div>
  )
}

function TabletTopBezel() {
  return <div className="h-3 shrink-0" style={{ borderBottom: CHROME_SEP }} />
}

function TabletBottomBezel() {
  return (
    <div className="flex shrink-0 justify-center pt-2 pb-0" style={{ borderTop: CHROME_SEP }}>
      <div className="h-2.5 w-2.5 rounded-sm" style={{ border: "1px solid rgba(255,255,255,0.18)" }} />
    </div>
  )
}

function MobileTopBezel() {
  return (
    <div className="flex shrink-0 justify-center pb-2" style={{ borderBottom: CHROME_SEP }}>
      <div className="h-1.5 w-8 rounded-full" style={{ border: "1px solid rgba(255,255,255,0.18)" }} />
    </div>
  )
}

function MobileHomeBar() {
  return (
    <div className="flex shrink-0 justify-center pt-2" style={{ borderTop: CHROME_SEP }}>
      <div className="h-1 w-10 rounded-full" style={{ border: "1px solid rgba(255,255,255,0.18)" }} />
    </div>
  )
}
